import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import useBlockClientCompanyBranch from "@/features/privateCompanies/hooks/useBlockClientCompanyBranch";
import useUnblockClientCompanyBranch from "@/features/privateCompanies/hooks/useUnblockClientCompanyBranch";
import { companyBranchBlockUnblockSchema, CompanyBranchBlockUnblockValidationSchema } from "@/features/privateCompanies/schemas/blockUnblockCompanyBranch.schema";
import { CompanyBranchData } from "@/features/privateCompanies/types/companyBranches.type";

const BlockUnblockCompanyBranch = ({ companyBranch }: { companyBranch?: CompanyBranchData; }) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [blockModalIsOpen, setBlockModalIsOpen] = useState(false);
  const [unblockModalIsOpen, setUnblockModalIsOpen] = useState(false);

  const { mutate: blockCompanyBranch } = useBlockClientCompanyBranch({
    companyBranchId: companyBranch?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyBranch', companyBranch?.id] });
      blockFormReset();
      setBlockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: blockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { mutate: unblockCompanyBranch } = useUnblockClientCompanyBranch({
    companyBranchId: companyBranch?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyBranch', companyBranch?.id] });
      unblockFormReset();
      setUnblockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: unblockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isDisabled = companyBranch?.status === 'disabled';

  const {
    register: blockFormRegister,
    reset: blockFormReset,
    setError: blockFormSetError,
    handleSubmit: blockFormHandleSubmit,
    formState: { errors: blockFormErrors },
  } = useForm<CompanyBranchBlockUnblockValidationSchema>({
    resolver: zodResolver(companyBranchBlockUnblockSchema),
  });

  const {
    register: unblockFormRegister,
    reset: unblockFormReset,
    setError: unblockFormSetError,
    handleSubmit: unblockFormHandleSubmit,
    formState: { errors: unblockFormErrors },
  } = useForm<CompanyBranchBlockUnblockValidationSchema>({
    resolver: zodResolver(companyBranchBlockUnblockSchema),
  });

  return (
    <>
      <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isDisabled && 'bg-[#FCCDCA33]')}>
        <div className="flex items-center gap-x-2">
          <InfoIcon strokeWidth={1} className="text-yellow-600" />

          <div>
            <p className="font-bold text-[10px] leading-3">
              {!isDisabled ? (
                'O bloqueio da filial impede todas transações.'
              ) : (
                'Desbloqueie a filial para permitir as transações.'
              )}
            </p>

            <p className="font-light text-[10px] italic leading-3">
              {!isDisabled ? (
                'Caso a filial seja bloqueada, ela ficará impossibilitada de transacionar.'
              ) : (
                'Caso a filial seja desbloqueada, transações poderão ser feitas.'
              )}
            </p>
          </div>
        </div>

        {!isDisabled ? (
          <Modal
            Trigger={(
              <Button operationKlassToCheckPermission="Admin::Client::CompanyBranches::Disable" type="button" data-testid='block_company_branch_button' variant="yellow-black">
                Bloquear Filial
              </Button>
            )}
            title="Você tem certeza?"
            subtitle="Caso a filial seja bloqueada, nenhuma transação poderá ser feita."
            subtitleClassName="mt-1 text-white-300"
            showClose={false}
            useCancellableFooter
            isOpen={blockModalIsOpen}
            setIsOpen={setBlockModalIsOpen}
            onConfirm={blockFormHandleSubmit((data) => blockCompanyBranch(data))}
            body={(
              <form className="flex flex-col gap-4">
                <TextField
                  className="text-white-800 opacity-80"
                  label="Motivo"
                  markAsRequired
                  {...blockFormRegister('reason')}
                  errorMessage={blockFormErrors.reason?.message || undefined}
                />
              </form>
            )}
          />
        ) : (
          <Modal
            Trigger={(
              <Button operationKlassToCheckPermission="Admin::Client::CompanyBranches::Enable" type="button" data-testid='unblock_company_branch_button'>
                Desbloquear Filial
              </Button>
            )}
            title="Você tem certeza?"
            subtitle="Se a filial for desbloqueada, transações poderão ser feitas."
            subtitleClassName="mt-1 text-white-300"
            showClose={false}
            useCancellableFooter
            isOpen={unblockModalIsOpen}
            setIsOpen={setUnblockModalIsOpen}
            onConfirm={unblockFormHandleSubmit((data) => unblockCompanyBranch(data))}
            body={(
              <form className="flex flex-col gap-4">
                <TextField
                  className="text-white-800 opacity-80"
                  label="Motivo"
                  markAsRequired
                  {...unblockFormRegister('reason')}
                  errorMessage={unblockFormErrors.reason?.message || undefined}
                />
              </form>
            )}
          />
        )}
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default BlockUnblockCompanyBranch;
